import React, { useRef, useState, useCallback, useEffect } from 'react';
import { toPng } from 'html-to-image';
import { LAYOUTS, FILTERS, THEMES } from './config';
import { useCamera, useCountdown, useVideoRecorder } from './hooks';
import PhotoFrame from './PhotoFrame';

const FRAME_WIDTH = 380;

export default function PhotoBooth({ onCapture, mobileMode = false }) {
  const isMobile = mobileMode || window.innerWidth < 640;
  const { videoRef, streamRef, camReady, camError, startCam, captureFrame } = useCamera();
  const { countdown, startCountdown } = useCountdown();
  const { recording, videoBlob, elapsed, startRecording, stopRecording, downloadVideo } = useVideoRecorder(streamRef);
  const frameRef  = useRef(null);
  const fileRef   = useRef(null);

  const [tab,    setTab]    = useState('camera'); // 'camera' | 'gallery' | 'video'
  const [layout, setLayout] = useState(LAYOUTS[0]);
  const [filter, setFilter] = useState(FILTERS[0]);
  const [theme,  setTheme]  = useState(THEMES[0]);

  const [photos,    setPhotos]    = useState([]);
  const [flash,     setFlash]     = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exported,  setExported]  = useState(null);

  const sessionRef = useRef(false);

  const triggerFlash = useCallback(() => { setFlash(true); setTimeout(() => setFlash(false), 320); }, []);

  const doShot = useCallback(() => {
    const url = captureFrame(filter.css);
    if (!url) return;
    triggerFlash();
    setPhotos(prev => [...prev, url]);
  }, [captureFrame, filter.css, triggerFlash]);

  const startSession = useCallback(() => {
    if (!camReady || countdown !== null || exporting) return;
    sessionRef.current = true;
    setPhotos([]); setExported(null);
    startCountdown(3, doShot);
  }, [camReady, countdown, exporting, startCountdown, doShot]);

  useEffect(() => {
    if (!sessionRef.current) return;
    if (photos.length > 0 && photos.length < layout.slots && countdown === null) {
      const t = setTimeout(() => startCountdown(3, doShot), 800);
      return () => clearTimeout(t);
    }
    if (photos.length >= layout.slots && countdown === null) {
      sessionRef.current = false;
      setTimeout(exportFrame, 300);
    }
  }, [photos.length, layout.slots, countdown]); // eslint-disable-line

  const exportFrame = useCallback(async () => {
    if (!frameRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(frameRef.current, { cacheBust: true, pixelRatio: 2 });
      setExported(dataUrl);
      onCapture(dataUrl);
    } catch (e) { console.error('Export failed', e); }
    finally { setExporting(false); }
  }, [onCapture]);

  const retake = () => { setPhotos([]); setExported(null); sessionRef.current = false; };

  const downloadPng = () => {
    if (!exported) return;
    const a = document.createElement('a'); a.href = exported; a.download = 'HBD-Nof-Photobooth.png'; a.click();
  };

  // ── GALLERY UPLOAD ──────────────────────────────────────────
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []).slice(0, layout.slots);
    if (!files.length) return;
    retake();
    const readers = files.map(file => new Promise(res => {
      const r = new FileReader();
      r.onload = ev => res(ev.target.result);
      r.readAsDataURL(file);
    }));
    Promise.all(readers).then(urls => {
      setPhotos(urls);
      // Auto-export after upload
      setTimeout(() => {
        sessionRef.current = false;
        // trigger export via effect by setting photos to full length
      }, 100);
    });
    // Reset input so same file can be re-selected
    e.target.value = '';
  }, [layout.slots]); // eslint-disable-line

  // When gallery photos fill all slots, auto-export
  useEffect(() => {
    if (tab === 'gallery' && photos.length > 0 && !sessionRef.current && !exported && !exporting) {
      setTimeout(exportFrame, 400);
    }
  }, [photos.length]); // eslint-disable-line

  const isCapturing = countdown !== null;
  const isDone = photos.length >= layout.slots;





  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <div style={{ display: 'flex', gap: 20, flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap' }}>

        {/* Left: camera / gallery / video + controls */}
        <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* View Tab Switcher */}
          <div style={{ display: 'flex', gap: 6, background: 'var(--surface)', borderRadius: 10, padding: 4 }}>
            {[
              { id: 'camera', label: '📸 Foto' },
              { id: 'video', label: '🎥 Video' },
              { id: 'gallery', label: '🖼️ Galeri' }
            ].map(t => (
              <button key={t.id}
                style={{ flex: 1, padding: '7px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, transition: 'all 0.2s', background: tab === t.id ? 'var(--terra)' : 'transparent', color: tab === t.id ? '#fff' : 'var(--text-dim)' }}
                onClick={() => { setTab(t.id); retake(); }}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'camera' ? (
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: 'var(--surface)', aspectRatio: '16/9', border: '1px solid var(--border)' }}>
                <video ref={videoRef} autoPlay playsInline muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)', filter: filter.css !== 'none' ? filter.css : undefined, display: (isDone && exported) ? 'none' : 'block' }} />

                {isDone && exported && (
                  <img src={exported} alt="result" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                )}
                {!isDone && photos.length > 0 && (
                  <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5 }}>
                    {Array.from({ length: layout.slots }).map((_, i) => (
                      <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: i < photos.length ? 'var(--terra)' : 'var(--border-warm)', transition: 'background 0.3s' }} />
                    ))}
                  </div>
                )}
                {isCapturing && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }}>
                    <span style={{ fontSize: 88, fontWeight: 900, color: 'var(--text)', fontFamily: "'Playfair Display', serif", textShadow: '0 0 25px rgba(193,123,90,0.7)' }}>{countdown}</span>
                  </div>
                )}
                {exporting && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' }}>
                    <span style={{ color: 'var(--amber)', fontFamily: 'DM Mono, monospace', fontSize: 13 }}>Rendering...</span>
                  </div>
                )}
                {flash && <div className="animate-flash" style={{ position: 'absolute', inset: 0, background: '#FFF9F0', zIndex: 10 }} />}
                {camError && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: 'var(--surface)', padding: 20, textAlign: 'center' }}>
                    <span style={{ fontSize: 32 }}>📷</span>
                    <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: 0 }}>{camError}</p>
                    <button className="btn-shoot" onClick={startCam} style={{ padding: '8px 20px', fontSize: 13 }}>Coba lagi</button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
                <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: 'var(--surface)', border: '2px dashed var(--border-warm)', minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, cursor: 'pointer', transition: 'border-color 0.2s' }}
                  onClick={() => !isDone && fileRef.current?.click()}>
                  {!isDone ? (
                    <>
                      <span style={{ fontSize: 40 }}>🖼️</span>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15, margin: '0 0 4px', fontFamily: "'DM Sans', sans-serif" }}>
                          Pilih Foto dari Galeri
                        </p>
                        <p style={{ color: 'var(--text-dim)', fontSize: 12, margin: 0, fontFamily: 'DM Mono, monospace' }}>
                          Pilih {layout.slots} foto sekaligus
                        </p>
                      </div>
                      <button className="btn-shoot" style={{ padding: '10px 24px', fontSize: 13 }} onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}>
                        📁 Buka Galeri
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Show selected photo thumbnails */}
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(layout.cols, photos.length)}, 1fr)`, gap: 6, padding: 12, width: '100%' }}>
                        {photos.map((src, i) => (
                          <div key={i} style={{ borderRadius: 8, overflow: 'hidden', aspectRatio: '4/3', background: 'var(--bg)' }}>
                            <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: filter.css !== 'none' ? filter.css : undefined }} />
                          </div>
                        ))}
                      </div>
                      {exporting && (
                        <div style={{ color: 'var(--amber)', fontFamily: 'DM Mono, monospace', fontSize: 13 }}>Membuat frame...</div>
                      )}
                    </>
                  )}
                </div>

                {/* Progress */}
                {photos.length > 0 && !isDone && (
                  <div style={{ height: 3, background: 'var(--border)', borderRadius: 4, overflow: 'hidden', marginTop: 8 }}>
                    <div style={{ height: '100%', width: `${(photos.length / layout.slots) * 100}%`, background: 'var(--terra)', transition: 'width 0.5s' }} />
                  </div>
                )}
              </div>
            )}

            {tab === 'video' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: 'var(--surface)', aspectRatio: '16/9', border: '1px solid var(--border)' }}>
                  <video ref={videoRef} autoPlay playsInline muted
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)', filter: filter.css !== 'none' ? filter.css : undefined, display: (videoBlob && !recording) ? 'none' : 'block' }} />
                  {videoBlob && !recording && (
                    <video src={URL.createObjectURL(videoBlob)} controls
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
                  )}
                  {recording && (
                    <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.65)', borderRadius: 20, padding: '4px 10px' }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', animation: 'pulse-dot 1s infinite' }} />
                      <span style={{ color: '#fff', fontSize: 11, fontFamily: 'DM Mono, monospace' }}>REC {elapsed}s / 30s</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Progress bar (camera mode) */}
            {tab === 'camera' && layout.slots > 1 && photos.length > 0 && !isDone && (
              <div style={{ height: 3, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(photos.length / layout.slots) * 100}%`, background: 'var(--terra)', transition: 'width 0.5s' }} />
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {tab === 'camera' && (
                !isDone ? (
                  <button className="btn-shoot" onClick={startSession} disabled={!camReady || isCapturing || exporting}>
                    {isCapturing ? `${countdown}...` : photos.length > 0 ? `📸 Foto ${photos.length + 1}/${layout.slots}` : '📸  Jepret!'}
                  </button>
                ) : (
                  <>
                    <button className="btn-secondary" onClick={retake}>↩ Ulangi</button>
                    <button className="btn-secondary" onClick={downloadPng} disabled={!exported}>⬇ Simpan PNG</button>
                  </>
                )
              )}
              {tab === 'gallery' && isDone && (
                <>
                  <button className="btn-secondary" onClick={() => { retake(); fileRef.current?.click(); }}>↩ Ganti Foto</button>
                  <button className="btn-secondary" onClick={downloadPng} disabled={!exported || exporting}>⬇ Simpan PNG</button>
                </>
              )}
              {tab === 'video' && (
                <>
                  {!recording
                    ? <button className="btn-shoot" onClick={() => startRecording(30)} disabled={!camReady}>⏺  Mulai Rekam</button>
                    : <button className="btn-shoot" style={{ background: '#7f1d1d' }} onClick={stopRecording}>⏹  Stop ({30 - elapsed}s)</button>}
                  {videoBlob && !recording && <button className="btn-secondary" onClick={downloadVideo}>⬇ Download Video</button>}
                </>
              )}
            </div>

            {/* Controls: Grid + Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="ctrl-section">
                <span className="ctrl-label">Grid Layout</span>
                <div className="ctrl-row">
                  {LAYOUTS.map(l => (
                    <button key={l.id} className={`pill${layout.id === l.id ? ' active' : ''}`}
                      onClick={() => { setLayout(l); retake(); }}>{l.label}</button>
                  ))}
                </div>
              </div>
              <div className="ctrl-section">
                <span className="ctrl-label">Filter</span>
                <div className="ctrl-row">
                  {FILTERS.map(f => (
                    <button key={f.id} className={`pill${filter.id === f.id ? ' active' : ''}`}
                      onClick={() => setFilter(f)}>{f.label}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Theme + Frame Preview */}
          <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="ctrl-section">
              <span className="ctrl-label">Tema Frame</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                {THEMES.map(t => (
                  <button key={t.id} className={`theme-card${theme.id === t.id ? ' active' : ''}`} onClick={() => setTheme(t)}>
                    <span className="theme-icon">{t.icon}</span>
                    <span className="theme-name">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="ctrl-section">
              <span className="ctrl-label">Preview Frame</span>
              <div style={{ overflowY: 'auto', maxHeight: '60vh' }}>
                <PhotoFrame ref={frameRef} layout={layout} theme={theme} photos={photos} filter={filter} width={FRAME_WIDTH} />
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}
